import { useState, useEffect, useCallback } from 'react'
import { trackPwaInstall } from '@/services/superAdminService'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Capture the beforeinstallprompt event globally so it's never missed
// (it can fire before React components mount)
let earlyPromptEvent: BeforeInstallPromptEvent | null = null
window.addEventListener('beforeinstallprompt', (e: Event) => {
  e.preventDefault()
  earlyPromptEvent = e as BeforeInstallPromptEvent
})

export function usePWAInstall(source: 'landing' | 'app' = 'app') {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(earlyPromptEvent)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
    }

    // Pick up early-captured prompt if we missed it
    if (earlyPromptEvent && !deferredPrompt) {
      setIsInstalled(false)
      setDeferredPrompt(earlyPromptEvent)
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      earlyPromptEvent = e as BeforeInstallPromptEvent
      setIsInstalled(false)
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    const handleAppInstalled = () => {
      earlyPromptEvent = null
      setIsInstalled(true)
      setDeferredPrompt(null)
      trackPwaInstall(source).catch(() => {
        // Silently ignore tracking errors
      })
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [source])

  const install = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setDeferredPrompt(null)
    }
  }, [deferredPrompt])

  return {
    canInstall: !!deferredPrompt && !isInstalled,
    isInstalled,
    install,
  }
}
